import React, { useMemo } from 'react';
import {
  Box, Text, Tooltip, TooltipContent, TooltipText, VStack,
} from '@gluestack-ui/themed';
import { BulletItem } from '@/common/components/BulletList';
import { OralExamFillKindBadge } from '@/common/components/OralExamFillKindBadge';
import {
  FILL_TOKEN,
  splitFillAnchor,
  type OralExamFillKind,
} from '@/common/components/oralExamPreviewQuestion';

const SELECTABLE_WEB_SX = { _web: { userSelect: 'text' as const } };

type ParsedLine =
  | { type: 'bullet'; text: string }
  | { type: 'clause'; text: string };

type ParsedQuestion = {
  title?: string;
  legacyBody?: string;
  lines: ParsedLine[];
  isLegacy: boolean;
};

type OralExamQuestionTextSize =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl';

export const ORAL_EXAM_QUESTION_TEXT_SIZES: readonly OralExamQuestionTextSize[] = [
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
] as const;

/** Title matches body size by default; hierarchy comes from bold weight. */
export const ORAL_EXAM_QUESTION_TITLE_SIZE_STEP_DOWN = 0;

function stepDownOralExamQuestionTextSize(
  size: OralExamQuestionTextSize,
  steps: number,
): OralExamQuestionTextSize {
  const index = ORAL_EXAM_QUESTION_TEXT_SIZES.indexOf(size);
  if (index < 0) return size;
  return ORAL_EXAM_QUESTION_TEXT_SIZES[Math.max(0, index - steps)];
}

export type OralExamQuestionTextProps = {
  question: string;
  size?: OralExamQuestionTextSize;
  /**
   * Optional explicit title size. When omitted, title matches ``size`` (same
   * as the body); hierarchy is bold weight. Pass a different size only when a
   * surface needs a deliberate title/body contrast.
   */
  titleSize?: OralExamQuestionTextSize;
  titleColor?: string;
  textColor?: string;
  /** Size and colour of the ``- `` prompts when they should read as secondary to the lead. */
  promptSize?: OralExamQuestionTextSize;
  promptColor?: string;
  numberOfLines?: number;
  /**
   * Collapsed pool rows: flatten title + sentence (including bullets) into one
   * inline preview. Prefer ``numberOfLines`` (clamped). When ``omitBullets`` is
   * set without ``numberOfLines``, bullets stay hidden and the lead stays
   * unclamped. Ignored when ``numberOfLines`` is set (flat preview wins).
   */
  omitBullets?: boolean;
  /** Suppresses the inline title row, for callers that already surface the title elsewhere (e.g. a card heading). */
  hideTitle?: boolean;
  /**
   * When set, the fill slot (``{{fill}}`` or the server-substituted kind cue)
   * is replaced inline with the universal slot pill. Pool/editor previews only;
   * live exam sessions leave this false so bound specimen text stays plain.
   */
  showFillSlot?: boolean;
  fillKind?: OralExamFillKind | string | null;
  /**
   * Examples for the fill slot. The first is written into the sentence and
   * underlined. The rest appear as bullets while that example is hovered.
   */
  fills?: string[] | null;
  fillKindBadgeTestID?: string;
  testID?: string;
};

const TITLE_SEPARATOR = ': ';

/**
 * Extracts the short title/topic embedded in a question's first line (text before the
 * first `: `), so callers can surface it independently of the full question body
 * (e.g. in a card heading like "Question 9 — Explainable UX Patterns").
 */
export function getOralExamQuestionTitle(question: string): string | undefined {
  const firstLine = (question ?? '').trim().split('\n')[0]?.trim() ?? '';
  const colonIndex = firstLine.indexOf(TITLE_SEPARATOR);
  return colonIndex >= 0 ? firstLine.slice(0, colonIndex) : undefined;
}

/** Collapse stored question text into one flowing preview line (strip `- ` markers). */
function flattenQuestionPreview(question: string): string {
  const trimmed = question.trim();
  if (!trimmed) return '';

  const colonIndex = trimmed.indexOf(TITLE_SEPARATOR);
  const prefix = colonIndex >= 0 ? `${trimmed.slice(0, colonIndex)}${TITLE_SEPARATOR}` : '';
  const body = colonIndex >= 0 ? trimmed.slice(colonIndex + TITLE_SEPARATOR.length) : trimmed;

  const parts: string[] = [];
  for (const line of body.split('\n')) {
    const stripped = line.trim();
    if (stripped) {
      parts.push(stripped.startsWith('- ') ? stripped.slice(2) : stripped);
    }
  }

  const spokenBody = parts.join(' ');
  return prefix ? `${prefix}${spokenBody}` : spokenBody;
}

function parseOralExamQuestion(question: string): ParsedQuestion | null {
  const trimmed = question.trim();
  if (!trimmed) return null;

  const rawLines = trimmed
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (rawLines.length === 0) return null;

  const firstLine = rawLines[0];
  const hasBullets = rawLines.some((line) => line.startsWith('- '));
  const colonIndex = firstLine.indexOf(TITLE_SEPARATOR);

  const title = colonIndex >= 0 ? firstLine.slice(0, colonIndex) : undefined;
  const firstLineRemainder = colonIndex >= 0
    ? firstLine.slice(colonIndex + TITLE_SEPARATOR.length)
    : firstLine;

  if (!hasBullets) {
    if (colonIndex >= 0) {
      return {
        title,
        legacyBody: firstLineRemainder || undefined,
        lines: [],
        isLegacy: true,
      };
    }

    return {
      legacyBody: firstLine,
      lines: [],
      isLegacy: true,
    };
  }

  const lines: ParsedLine[] = [];
  if (firstLineRemainder) {
    lines.push({ type: 'clause', text: firstLineRemainder });
  }

  for (let index = 1; index < rawLines.length; index += 1) {
    const line = rawLines[index];
    if (line.startsWith('- ')) {
      lines.push({ type: 'bullet', text: line.slice(2) });
    } else {
      lines.push({ type: 'clause', text: line });
    }
  }

  return {
    title,
    lines,
    isLegacy: false,
  };
}

export type OralExamQuestionParts = {
  title?: string;
  paragraphs: string[];
  prompts: string[];
};

/** Title, running-text paragraphs, and `- ` prompts of a stored question, for editorial layouts. */
export function getOralExamQuestionParts(question: string): OralExamQuestionParts | null {
  const parsed = parseOralExamQuestion(question ?? '');
  if (!parsed) return null;
  if (parsed.isLegacy) {
    return {
      title: parsed.title,
      paragraphs: parsed.legacyBody ? [parsed.legacyBody] : [],
      prompts: [],
    };
  }
  return {
    title: parsed.title,
    paragraphs: parsed.lines.filter((line) => line.type === 'clause').map((line) => line.text),
    prompts: parsed.lines.filter((line) => line.type === 'bullet').map((line) => line.text),
  };
}

type FillAwareLineProps = {
  text: string;
  size: OralExamQuestionTextSize;
  color: string;
  showFillSlot?: boolean;
  fillKind?: OralExamFillKind | string | null;
  fills?: string[] | null;
  badgeTestID?: string;
  testID?: string;
  flex?: number;
  /** Bold title rendered inline before ``text`` (``Title: ``). */
  title?: string;
  titleSize?: OralExamQuestionTextSize;
  titleColor?: string;
  numberOfLines?: number;
};

function splitQuestionFillAnchor(
  text: string,
  showFillSlot: boolean,
  fillKind?: OralExamFillKind | string | null,
) {
  return splitFillAnchor(
    text,
    showFillSlot ? fillKind : null,
    showFillSlot ? { allowUniversalCue: true } : undefined,
  );
}

function TitlePrefix({
  title,
  size,
  color,
  withSeparator,
}: {
  title?: string;
  size: OralExamQuestionTextSize;
  color: string;
  withSeparator: boolean;
}) {
  if (!title) return null;
  return (
    <>
      <Text
        testID="oral-exam-question-title"
        size={size}
        color={color}
        fontWeight="bold"
        selectable
        sx={SELECTABLE_WEB_SX}
      >
        {title}
      </Text>
      {withSeparator ? TITLE_SEPARATOR : null}
    </>
  );
}

/**
 * Render one question line, swapping the fill slot for the universal slot pill when present.
 * Nested ``Text`` (not HStack) so title + sentence stay one selectable run that wraps.
 */
function cleanFills(fills: string[] | null | undefined): string[] {
  return (fills ?? []).map((fill) => fill.trim()).filter((fill) => fill.length > 0);
}

function FillExample({
  example,
  alternatives,
  size,
  color,
}: {
  example: string;
  alternatives: string[];
  size: OralExamQuestionTextSize;
  color: string;
}) {
  if (alternatives.length === 0) {
    return (
      <Text
        size={size}
        color={color}
        textDecorationLine="underline"
        testID="oral-exam-question-fill-example"
      >
        {example}
      </Text>
    );
  }
  return (
    <Tooltip
      placement="bottom"
      trigger={(triggerProps: Record<string, unknown>) => (
        <Text
          // Gluestack's tooltip trigger props are an untyped hover/ref bag.
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...triggerProps}
          size={size}
          color={color}
          textDecorationLine="underline"
          testID="oral-exam-question-fill-example"
          sx={{ _web: { cursor: 'help' } }}
        >
          {example}
        </Text>
      )}
    >
      <TooltipContent testID="oral-exam-question-fill-alternatives">
        <VStack space="xs">
          {alternatives.map((alternative) => (
            <TooltipText key={alternative} size="xs">
              {`• ${alternative}`}
            </TooltipText>
          ))}
        </VStack>
      </TooltipContent>
    </Tooltip>
  );
}

function FillAwareLine({
  text,
  size,
  color,
  showFillSlot = false,
  fillKind,
  fills,
  badgeTestID,
  testID,
  flex,
  title,
  titleSize,
  titleColor,
  numberOfLines,
}: FillAwareLineProps) {
  const examples = cleanFills(fills);
  const firstExample = examples[0];
  const tokenIndex = firstExample ? text.indexOf(FILL_TOKEN) : -1;
  const split = tokenIndex >= 0 ? null : splitQuestionFillAnchor(text, showFillSlot, fillKind);
  return (
    <Text
      testID={testID}
      flex={flex}
      size={size}
      color={color}
      selectable
      numberOfLines={numberOfLines}
      ellipsizeMode={numberOfLines != null ? 'tail' : undefined}
      sx={{
        _web: {
          userSelect: 'text',
          ...(numberOfLines != null
            ? {
              display: '-webkit-box !important',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: numberOfLines,
              overflow: 'hidden',
            }
            : {}),
        },
      }}
    >
      <TitlePrefix
        title={title}
        size={titleSize ?? size}
        color={titleColor ?? color}
        withSeparator={text.length > 0}
      />
      {tokenIndex >= 0 && firstExample ? (
        <>
          {text.slice(0, tokenIndex)}
          <FillExample
            example={firstExample}
            alternatives={examples.slice(1)}
            size={size}
            color={color}
          />
          {text.slice(tokenIndex + FILL_TOKEN.length)}
        </>
      ) : split ? (
        <>
          {split.before}
          <OralExamFillKindBadge testID={badgeTestID} />
          {split.after}
        </>
      ) : (
        text
      )}
    </Text>
  );
}

/**
 * OralExamQuestionText
 * ----
 * Parses oral-exam question strings (title + lead + `- ` bullets). Collapsed /
 * ``numberOfLines`` is one selectable inline sentence; expanded keeps title +
 * lead on the first line, then bullet rows. Optional ``fillKind`` replaces the
 * fill slot with an inline badge.
 */
export function OralExamQuestionText({
  question,
  size = 'md',
  titleSize: titleSizeProp,
  titleColor = '$textHeading',
  textColor = '$textBody',
  promptSize,
  promptColor,
  numberOfLines,
  omitBullets = false,
  hideTitle = false,
  showFillSlot = false,
  fillKind,
  fills,
  fillKindBadgeTestID,
  testID,
}: OralExamQuestionTextProps) {
  const parsed = useMemo(() => parseOralExamQuestion(question ?? ''), [question]);
  const flatPreview = useMemo(() => flattenQuestionPreview(question ?? ''), [question]);
  const titleSize = titleSizeProp ?? stepDownOralExamQuestionTextSize(
    size,
    ORAL_EXAM_QUESTION_TITLE_SIZE_STEP_DOWN,
  );
  const fillLineProps = {
    size,
    color: textColor,
    showFillSlot,
    fillKind,
    fills,
    badgeTestID: fillKindBadgeTestID,
    titleSize,
    titleColor,
  };

  if (!parsed || !flatPreview) {
    return null;
  }

  if (numberOfLines != null) {
    const colonIndex = flatPreview.indexOf(TITLE_SEPARATOR);
    const title = !hideTitle && colonIndex >= 0 ? flatPreview.slice(0, colonIndex) : undefined;
    const body = colonIndex >= 0
      ? flatPreview.slice(colonIndex + TITLE_SEPARATOR.length)
      : flatPreview;
    return (
      <FillAwareLine
        {...fillLineProps}
        text={body}
        title={title}
        testID={testID ?? 'oral-exam-question-flat'}
        flex={1}
        numberOfLines={numberOfLines}
      />
    );
  }

  if (parsed.isLegacy) {
    if (parsed.title && parsed.legacyBody) {
      return (
        <FillAwareLine
          {...fillLineProps}
          text={parsed.legacyBody}
          title={hideTitle ? undefined : parsed.title}
          testID={testID ?? 'oral-exam-question-body'}
        />
      );
    }

    if (parsed.title && !parsed.legacyBody) {
      if (hideTitle) return null;
      return (
        <FillAwareLine
          {...fillLineProps}
          text=""
          title={parsed.title}
          testID={testID}
        />
      );
    }

    return (
      <FillAwareLine
        {...fillLineProps}
        text={parsed.legacyBody ?? ''}
        testID={testID ?? 'oral-exam-question-body'}
      />
    );
  }

  let bulletIndex = 0;
  const lines = omitBullets
    ? parsed.lines.filter((line) => line.type !== 'bullet')
    : parsed.lines;
  const firstClause = lines.find((line) => line.type === 'clause');

  return (
    <VStack space="xs" overflow="visible" testID={testID}>
      {!firstClause && !hideTitle && parsed.title ? (
        <FillAwareLine {...fillLineProps} text="" title={parsed.title} />
      ) : null}
      {lines.map((line) => {
        if (line.type === 'bullet') {
          const currentBulletIndex = bulletIndex;
          bulletIndex += 1;
          const bullet = (
            <BulletItem
              key={`bullet-${currentBulletIndex}`}
              size={promptSize ?? size}
              color={promptColor ?? textColor}
              selectable
              testID={`oral-exam-question-bullet-${currentBulletIndex}`}
            >
              <FillAwareLine
                {...fillLineProps}
                size={promptSize ?? size}
                color={promptColor ?? textColor}
                text={line.text}
                flex={1}
              />
            </BulletItem>
          );
          // Secondary prompts need air above them, or they read as a wrapped lead.
          if (promptSize && currentBulletIndex === 0) {
            return <Box key={`bullet-${currentBulletIndex}`} pt="$1.5">{bullet}</Box>;
          }
          return bullet;
        }

        const isLead = line === firstClause;
        return (
          <FillAwareLine
            key={`clause-${line.text}`}
            {...fillLineProps}
            text={line.text}
            title={isLead && !hideTitle ? parsed.title : undefined}
            testID={isLead ? 'oral-exam-question-lead' : undefined}
          />
        );
      })}
    </VStack>
  );
}

export default OralExamQuestionText;
